import React, { useCallback, useRef, useState, MouseEvent } from 'react'
import Card from '../../atoms/card/Card'
import Clickable from '../../atoms/clickable/Clickable'
import Icon from '../../atoms/icon/Icon'

import InlineGroup from '../../atoms/inlinegroup/InlineGroup'
import EditableText, { Handles } from '../../atoms/text/EditableText'
import Text from '../../atoms/text/Text'
import VerticalGroup from '../../atoms/verticalgroup/VerticalGroup'
import DisconnectCEODevice from './DisconnectCEODevice'
import { formatTeamName } from './utils/getTeamName'
import API from '../../../services/api'

interface Props {
  gameId: string
  teams: API.GameConnection[]
  event: API.EventResponse
}

interface UIState {
  disconnectModalOpen: boolean
  teamSelected: API.GameConnection | null
}

const TeamItem: React.FC<{ team: API.GameConnection, setUiState: any, event: API.EventResponse }> = ({ team, setUiState, event }) => {
  const nameEditorRef = useRef<Handles>(null)

  const teamHasMaster = team.devices.some(d => d.isMaster)

  const onEditTeamIconClicked = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (nameEditorRef.current) {
      nameEditorRef.current.setIsEditing(true)
    }
  }, [])

  const onTeamNameUpdated = useCallback((newTeamName: string) => {
    // send api calls
    API.updateTeamInfo(event.id, team.teamId, {
      name: newTeamName,
      strategy: team.strategy || ''
    }).catch((e:any) => {
      console.error(e)
    })
  }, [event.id, team.strategy, team.teamId])

  return <InlineGroup verticalCenter block evenWidthChildren key={`team-${team.teamId}`}>
    <InlineGroup verticalCenter spaceLeft={2}>
      <Clickable disabled={!teamHasMaster} centered onClick={(event) => {
        event.stopPropagation()
        setUiState({ disconnectModalOpen: true, teamSelected: team })
      }}>
        <Icon type='disconnect' colour={teamHasMaster ? 'danger' : 'grey1'} noMargin tip={{
          content: (
            <>
              <p>This will disconnect the CEO from the simulation</p>
            </>
          ), id: `${team.teamId}-disconnect`
        }}/>
      </Clickable>

      <EditableText size='sm' type="text" ref={nameEditorRef} value={team.name} formatter={formatTeamName(team)} onChange={onTeamNameUpdated}/>
      <Clickable centered onClick={onEditTeamIconClicked}>
        <Icon type='edit' noMargin tip={{
          content: (
            <>
              <p>Edit team name</p>
            </>
          ), id: `${team}-edit-team-name`
        }} />
      </Clickable>
    </InlineGroup>
    <InlineGroup evenWidthChildren verticalCenter>
      <Icon noMargin type="circle" colour={team.connected ? 'lightgreen' : 'grey1'} tip={{
        content: (
          <>
            <p>The “Connected” status indicates whether <br />
                    the CEO for this team is currently logged <br />
                    in and "connected" to the simulation.
            </p>
          </>
        ), id: `${team.teamId}-connected`
      }} />
      <Icon noMargin type="status" colour={team.submitted ? 'lightgreen' : 'grey1'} tip={{
        content: (
          <>
            <p>The “Decisions” status indicates whether <br />
                  this team has submitted decisions.</p>
          </>
        ), id: `${team.teamId}-status`
      }} />
    </InlineGroup>
  </InlineGroup>
}

function GameConnections({ teams, gameId, event }: Props) {
  const [{ disconnectModalOpen, teamSelected }, setUiState] = useState<UIState>({ disconnectModalOpen: false, teamSelected: null })

  const onDisconnectDeviceComplete = useCallback(() => {
    setUiState(prevState => ({ ...prevState, disconnectModalOpen: false, teamSelected: null }))
  }, [])

  return (
    <Card wide flex>
      {disconnectModalOpen && !!teamSelected && <DisconnectCEODevice
        gameId={gameId}
        isOpen={disconnectModalOpen}
        onClose={() => setUiState(prevState => ({ ...prevState, disconnectModalOpen: false, teamSelected: null }))}
        onComplete={onDisconnectDeviceComplete}
        team={teamSelected}
      />}
      <InlineGroup block evenWidthChildren verticalCenter>
        <Text bold size='sm'>Teams</Text>
        <InlineGroup evenWidthChildren>
          <Text bold size='sm' center>Connected</Text>
          <Text bold size='sm' center>Decisions</Text>
        </InlineGroup>
      </InlineGroup>
      <VerticalGroup className='mt-6' spaceBetweenElements={6}>
        {teams.map(team => {
          return <TeamItem setUiState={setUiState} team={team} key={team.teamId} event={event} /> 
        })}
      </VerticalGroup>
    </Card>
  )
}

export default GameConnections
